import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';

import { GetEntityById2GQL, GetEntityConnectionsQuery, NoteTarget } from 'generated/graphql';

import { NoteDialogComponent } from 'app/note/dialogs/note-dialog/note-dialog.component';
import { ToastrService } from 'core/toastr.service';
import { DeleteEntityConnectionService } from 'entity/helpers/delete-entity-connection.service';
import {
  UpdateEntityConnectionStatusService
} from 'entity/helpers/update-entity-connection-status.service';
import {
  ApplyTagWithFieldSelectDialog
} from 'field-shared/dialogs/apply-tag-with-field-select/apply-tag-with-field-select.dialog';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';

@Component({
  selector: 'app-entity-connection-row',
  templateUrl: './entity-connection-row.component.html',
  styleUrls: ['./entity-connection-row.component.scss']
})
export class EntityConnectionRowComponent implements OnInit, OnChanges {
  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() entityConnectionTypeId: string;
  @Input() entityConnection: GetEntityConnectionsQuery['getEntityConnections']['nodes'][0];

  @Input() notesExpanded = false;
  @Input() infoExpanded = false;

  connectedToEntity: GetEntityConnectionsQuery['getEntityConnections']['nodes'][0]['entity1'];

  get anyExpanded() {
    return this.notesExpanded || this.infoExpanded;
  }

  constructor(
    private _cd: ChangeDetectorRef,
    private _toastrService: ToastrService,
    private _dialog: MatDialog,
    private _router: Router,
    private _getEntityGQL: GetEntityById2GQL,
    private _updateEntityConnectionSvc: UpdateEntityConnectionStatusService,
    private _deleteEntityConnectionSvc: DeleteEntityConnectionService
  ) { }

  ngOnInit(): void {
    this.connectedToEntity = this._getConnectedToEntity(this.entityId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId && !changes.entityId.isFirstChange()) {
      this.connectedToEntity = this._getConnectedToEntity(changes.entityId.currentValue);
    }
  }

  onViewProfileClicked(entityId) {
    // Prefetch data.
    this._getEntityGQL.fetch({
      entityId,
      campaignId: this.campaignId,
      withBestAddress: true,
      withToplines: true,
      withBestPhone: true,
      withBestEmail: true
    }).pipe(
      switchMap((res) => this._router.navigate(['/entity/view', res.data.getEntity.id], {
        queryParamsHandling: 'preserve'
      }))
    ).subscribe(() => { });
  }

  updateStatus(active: boolean) {
    this._updateEntityConnectionSvc.updateEntityConnectionStatus(
      {
        campaignId: this.campaignId,
        entityConnectionId: this.entityConnection.id,
        status: active
      }
    )
      .subscribe(
        (_) => {
          this._toastrService.success('Changes saved');
        }
      );
  }

  deleteConnection() {
    ConfirmDialogComponent.open(this._dialog, {
      data: {
        message: `Are you sure you want to remove the connection between the two entities?
                Please note that if a connection is recreated in the future between the same two entities,
                this connection (include notes and tags) will be restored.
                `
      }
    }).afterClosed().pipe(
      filter((proceed) => proceed),
      switchMap(() => this._deleteEntityConnectionSvc.deleteEntityConnection(
        this.campaignId,
        this.entityConnection
      ))
    ).subscribe((res) => {
      this._toastrService.success('Connection Removed');
    });
  }

  notesClicked() {
    if (this.entityConnection.notes.length === 0) {
      const ref = this.openAddNotesDialog();
      ref.afterClosed().subscribe(
        (event) => {
          if (event) {
            this.notesExpanded = true;
            this._cd.markForCheck();
          }
        }
      );
    } else {
      this.notesExpanded = true;
    }

    this._cd.markForCheck();
  }

  openAddNotesDialog() {
    const ref = NoteDialogComponent.open(this._dialog, {
      data: {
        targetId: this.entityConnection.id,
        campaignId: this.entityConnection.campaignId,
        targetType: NoteTarget.EntityConnection,
        noteType: 'text',
        hideNotes: true,
        heading: 'Add Note',
        keepOpenAfterSave: false
      }
    });

    return ref;
  }

  openAddConnectionInfoDialog() {
    return ApplyTagWithFieldSelectDialog.open(this._dialog, {
      data: {
        targetType: 'EntityConnectionType',
        targetTypeId: this.entityConnectionTypeId,
        targetId: this.entityConnection?.id,
        campaignId: this.campaignId
      }
    });
  }

  private _getConnectedToEntity(entityId: string) {
    return [
      this.entityConnection.entity1,
      this.entityConnection.entity2
    ].find((et) => entityId !== et.id);
  }
}
